/**
 * Firestore Utility Functions
 * Shared utilities for handling Firestore data conversions across mobile and web
 */

/**
 * Safely converts Firestore timestamps to Date objects
 * Handles multiple formats:
 * - Firestore Timestamp objects (with .toDate() method)
 * - JavaScript Date objects
 * - Serialized timestamps (with seconds field)
 * - Date strings
 *
 * @param field - The timestamp field to convert
 * @returns A Date object, or current date if field is null/undefined
 */
export function toDate(field: any): Date {
  if (!field) return new Date();
  if (field instanceof Date) return field;
  if (typeof field.toDate === 'function') return field.toDate();
  if (field.seconds) {
    // Handle both full serialized timestamps with nanoseconds and simple {seconds} objects
    const nanoseconds = field.nanoseconds || 0;
    return new Date(field.seconds * 1000 + nanoseconds / 1000000);
  }
  return new Date(field); // Try to parse as date string
}

/**
 * Safely converts Firestore timestamps to Date objects (nullable version)
 * Same as toDate but returns undefined instead of a fallback Date
 *
 * @param field - The timestamp field to convert
 * @returns A Date object, or undefined if field is null/undefined
 */
export function toDateNullable(field: any): Date | undefined {
  if (!field) return undefined;
  if (field instanceof Date) return field;
  if (typeof field.toDate === 'function') return field.toDate();
  if (field.seconds) {
    const nanoseconds = field.nanoseconds || 0;
    return new Date(field.seconds * 1000 + nanoseconds / 1000000);
  }
  try {
    return new Date(field);
  } catch {
    return undefined;
  }
}

/**
 * Normalizes a companyId value to a string
 * Handles cases where companyId might be a DocumentReference object
 *
 * @param value - The companyId value (string or DocumentReference)
 * @returns The normalized string ID, or null if invalid
 */
export function normalizeCompanyId(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'id' in value && typeof (value as { id: unknown }).id === 'string') {
    return (value as { id: string }).id;
  }
  return null;
}

/**
 * Convert document data with common timestamp fields
 * Automatically converts createdAt, updatedAt, lastPasswordChange, scheduledStart, and scheduledEnd fields
 * Also normalizes companyId if it's a DocumentReference
 * Filters out uid and id fields which are typically set separately
 *
 * @param data - Raw Firestore document data
 * @returns Converted document data with Date objects for timestamp fields
 */
export function convertDocumentTimestamps<T = Record<string, unknown>>(
  data: Record<string, unknown>
): Partial<T> {
  // Extract uid and id fields and exclude them from the result
  // These fields are often Firestore internal IDs that shouldn't be in the document data
  const { uid: _uid, id: _id, ...rest } = data || {};

  // Safely convert timestamp fields
  const createdAt = toDateNullable(data?.createdAt);
  const updatedAt = toDateNullable(data?.updatedAt);
  const lastPasswordChange = toDateNullable(data?.lastPasswordChange);
  const scheduledStart = toDateNullable(data?.scheduledStart);
  const scheduledEnd = toDateNullable(data?.scheduledEnd);
  const actualClockIn = toDateNullable(data?.actualClockIn);
  const actualClockOut = toDateNullable(data?.actualClockOut);
  const scheduledClockIn = toDateNullable(data?.scheduledClockIn);
  const scheduledClockOut = toDateNullable(data?.scheduledClockOut);
  const effectiveFrom = toDateNullable(data?.effectiveFrom);
  const effectiveUntil = toDateNullable(data?.effectiveUntil);

  // Normalize companyId if present (handles DocumentReference objects)
  const companyId = normalizeCompanyId(data?.companyId);

  return {
    ...rest,
    ...(createdAt && { createdAt }),
    ...(updatedAt && { updatedAt }),
    ...(lastPasswordChange && { lastPasswordChange }),
    ...(scheduledStart && { scheduledStart }),
    ...(scheduledEnd && { scheduledEnd }),
    ...(actualClockIn && { actualClockIn }),
    ...(actualClockOut && { actualClockOut }),
    ...(scheduledClockIn && { scheduledClockIn }),
    ...(scheduledClockOut && { scheduledClockOut }),
    ...(effectiveFrom && { effectiveFrom }),
    ...(effectiveUntil && { effectiveUntil }),
    ...(companyId && { companyId }),
  } as Partial<T>;
}
